import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import { Search, Activity, Wifi, Calendar, MapPin, Info, Key, Lock, CheckCircle, XCircle, FileText, Code } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

// XML to JSON parser function
const parseXmlToJson = (xmlString) => {
  try {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

    const parseError = xmlDoc.querySelector('parsererror');
    if (parseError) {
      return { error: 'Failed to parse XML' };
    }

    const xmlToJson = (node) => {
      const obj = {};

      // Handle attributes
      if (node.attributes && node.attributes.length > 0) {
        obj['@attributes'] = {};
        for (let i = 0; i < node.attributes.length; i++) {
          const attr = node.attributes[i];
          obj['@attributes'][attr.name] = attr.value;
        }
      }

      // Handle child nodes
      if (node.hasChildNodes()) {
        for (let i = 0; i < node.childNodes.length; i++) {
          const child = node.childNodes[i];
          const nodeName = child.nodeName;

          if (child.nodeType === 3) { // Text node
            const text = child.nodeValue.trim();
            if (text) {
              return text;
            }
          } else if (child.nodeType === 1) { // Element node
            if (obj[nodeName]) {
              if (!Array.isArray(obj[nodeName])) {
                obj[nodeName] = [obj[nodeName]];
              }
              obj[nodeName].push(xmlToJson(child));
            } else {
              obj[nodeName] = xmlToJson(child);
            }
          }
        }
      }

      return Object.keys(obj).length === 0 ? null : obj;
    };

    return xmlToJson(xmlDoc.documentElement);
  } catch (error) {
    console.error('XML parsing error:', error);
    return { error: error.message };
  }
};

// Convert JSON to simple text format
const jsonToText = (data, indent = 0) => {
  const spaces = '  '.repeat(indent);
  let text = '';

  if (!data || typeof data !== 'object') {
    return `${spaces}${String(data)}\n`;
  }

  if (Array.isArray(data)) {
    data.forEach((item, index) => {
      if (typeof item === 'object') {
        text += `${spaces}[${index + 1}]:\n`;
        text += jsonToText(item, indent + 1);
      } else {
        text += `${spaces}[${index + 1}]: ${item}\n`;
      }
    });
    return text;
  }

  Object.entries(data).forEach(([key, value]) => {
    if (key === '@attributes') return;

    if (typeof value === 'object') {
      text += `${spaces}${key}:\n`;
      text += jsonToText(value, indent + 1);
    } else {
      text += `${spaces}${key}: ${value}\n`;
    }
  });

  return text;
};

const CytaApi = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('connection');
  const [results, setResults] = useState(null);
  const [viewMode, setViewMode] = useState('readable'); // 'readable' or 'xml'

  // Authentication states
  const [tokenInfo, setTokenInfo] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);

  // Load saved token info on mount
  useEffect(() => {
    // Load token info
    const savedToken = localStorage.getItem('cyta_token_info');
    if (savedToken) {
      try {
        const tokenData = JSON.parse(savedToken);
        // Check if token is still valid
        if (tokenData.expiration_time && tokenData.expiration_time > Date.now()) {
          setTokenInfo(tokenData);
        } else {
          // Token expired, clear it
          localStorage.removeItem('cyta_token_info');
        }
      } catch (e) {
        console.error('Failed to parse saved token');
        localStorage.removeItem('cyta_token_info');
      }
    }
  }, []);

  // Countdown timer for token expiration - update every 5 seconds to reduce renders
  useEffect(() => {
    if (!tokenInfo || tokenInfo.error) {
      setTimeRemaining(null);
      return;
    }

    // Use stored expiration time or calculate new one
    const expirationTime = tokenInfo.expiration_time || (Date.now() + (tokenInfo.expires_in * 1000));

    // Set initial remaining time
    const initialRemaining = expirationTime - Date.now();
    if (initialRemaining > 0) {
      setTimeRemaining(initialRemaining);
    }

    const timer = setInterval(() => {
      const remaining = expirationTime - Date.now();

      if (remaining <= 0) {
        setTimeRemaining(null);
        setTokenInfo(null);
        localStorage.removeItem('cyta_token_info');
        toast.error('Token has expired. Please get a new token.');
        clearInterval(timer);
      } else {
        setTimeRemaining(remaining);
      }
    }, 5000); // Update every 5 seconds instead of 1 second

    return () => clearInterval(timer);
  }, [tokenInfo]);

  // Format time remaining as MM:SS
  const formatTimeRemaining = (ms) => {
    if (!ms) return '00:00';
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  // Form states
  const [addressForm, setAddressForm] = useState({ PostalCode: '' });
  const [fiberForm, setFiberForm] = useState({
    StreetID: '',
    StreetNumber: '',
    greek_flat_number: ''
  });
  const [ontPhysicalForm, setOntPhysicalForm] = useState({ CircuitNumber: '' });
  const [ontEthForm, setOntEthForm] = useState({ CircuitNumber: '' });
  const [connectionForm, setConnectionForm] = useState({ CircuitNo: '', CustSerialNumber: '744136' });
  const [plannedDateForm, setPlannedDateForm] = useState({ latitude: '', longitude: '' });

  // Read circuit number from URL params and pre-fill the form
  useEffect(() => {
    const circuitParam = searchParams.get('circuit');
    if (circuitParam) {
      setConnectionForm(prev => ({ ...prev, CircuitNo: circuitParam }));
      setActiveTab('connection');
      // Clear the URL param after reading it
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Token mutation (credentials now handled by backend)
  const tokenMutation = useMutation(
    () => api.post('/cyta/token'),
    {
      onSuccess: (response) => {
        const tokenData = {
          ...response.data.data,
          expiration_time: Date.now() + (response.data.data.expires_in * 1000)
        };
        setTokenInfo(tokenData);
        // Save to localStorage for persistence
        localStorage.setItem('cyta_token_info', JSON.stringify(tokenData));
        toast.success('Access token acquired successfully!');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to get access token');
        setTokenInfo({ error: error.response?.data?.message || 'Token request failed' });
      }
    }
  );

  const handleGetToken = (e) => {
    e.preventDefault();
    setTokenInfo(null);
    tokenMutation.mutate();
  };

  const handleClearToken = () => {
    localStorage.removeItem('cyta_token_info');
    setTokenInfo(null);
    setTimeRemaining(null);
    toast.success('Token cleared');
  };

  // API mutations
  const addressMutation = useMutation(
    (data) => api.post('/cyta/address-list', data),
    {
      onSuccess: (response) => {
        setResults(response.data.data);
        toast.success('Address list retrieved successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to fetch addresses');
        setResults({ error: error.response?.data?.message || 'Request failed' });
      }
    }
  );

  const fiberMutation = useMutation(
    (data) => api.post('/cyta/search-fiber', data),
    {
      onSuccess: (response) => {
        setResults(response.data.data);
        toast.success('Fiber search completed');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to search fiber');
        setResults({ error: error.response?.data?.message || 'Request failed' });
      }
    }
  );

  const ontPhysicalMutation = useMutation(
    (data) => api.post('/cyta/ont-physical-status', data),
    {
      onSuccess: (response) => {
        setResults(response.data.data);
        toast.success('ONT physical status retrieved');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to get ONT status');
        setResults({ error: error.response?.data?.message || 'Request failed' });
      }
    }
  );

  const ontEthMutation = useMutation(
    (data) => api.post('/cyta/ont-eth-status', data),
    {
      onSuccess: (response) => {
        setResults(response.data.data);
        toast.success('ONT Ethernet status retrieved');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to get Ethernet status');
        setResults({ error: error.response?.data?.message || 'Request failed' });
      }
    }
  );

  const connectionMutation = useMutation(
    (data) => api.post('/cyta/connection-details', data),
    {
      onSuccess: (response) => {
        setResults(response.data.data);
        toast.success('Connection details retrieved');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to get connection details');
        setResults({ error: error.response?.data?.message || 'Request failed' });
      }
    }
  );

  const plannedDateMutation = useMutation(
    (data) => api.post('/cyta/planned-date', data),
    {
      onSuccess: (response) => {
        setResults(response.data.data);
        toast.success('Planned date retrieved');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to get planned date');
        setResults({ error: error.response?.data?.message || 'Request failed' });
      }
    }
  );

  const handleSubmit = (e, type) => {
    e.preventDefault();
    setResults(null);

    switch (type) {
      case 'address':
        addressMutation.mutate(addressForm);
        break;
      case 'fiber':
        fiberMutation.mutate(fiberForm);
        break;
      case 'ontPhysical':
        ontPhysicalMutation.mutate(ontPhysicalForm);
        break;
      case 'ontEth':
        ontEthMutation.mutate(ontEthForm);
        break;
      case 'connection':
        connectionMutation.mutate(connectionForm);
        break;
      case 'plannedDate':
        plannedDateMutation.mutate(plannedDateForm);
        break;
      default:
        break;
    }
  };

  const tabs = [
    { id: 'connection', name: 'Connection Details', icon: Info },
    { id: 'fiber', name: 'Fiber Search', icon: Search },
    { id: 'ontPhysical', name: 'ONT Physical', icon: Activity },
    { id: 'ontEth', name: 'ONT Ethernet', icon: Wifi },
    { id: 'address', name: 'Address List', icon: MapPin },
    { id: 'plannedDate', name: 'Planned Date', icon: Calendar }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">CYTA Fiber API</h1>
        <p className="text-gray-600 mt-1">Check fiber availability, ONT status, and connection details</p>
      </div>

      {/* Authentication Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Key size={20} className="text-primary-500" />
            Authentication
          </h2>
        </div>

        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> CYTA API credentials are now securely stored on the server.
              Click the button below to get an access token.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleGetToken}
              disabled={tokenMutation.isLoading}
              className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Lock size={16} />
              {tokenMutation.isLoading ? 'Getting Token...' : 'Get Access Token'}
            </button>
            {tokenInfo && !tokenInfo.error && (
              <button
                type="button"
                onClick={handleClearToken}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
              >
                Clear Token
              </button>
            )}
          </div>

          {/* Token Status */}
          {tokenInfo && (
            <div className={`mt-4 p-4 rounded-lg ${tokenInfo.error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {tokenInfo.error ? (
                    <>
                      <XCircle size={20} className="text-red-600" />
                      <span className="font-semibold text-red-800">Failed to acquire token</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle size={20} className="text-green-600" />
                      <span className="font-semibold text-green-800">Token Acquired Successfully!</span>
                    </>
                  )}
                </div>
                {timeRemaining && (
                  <div className="flex items-center gap-2 bg-white px-3 py-1 rounded-md border border-green-300">
                    <span className="text-xs text-gray-600">Expires in:</span>
                    <span className="text-lg font-mono font-bold text-green-700">
                      {formatTimeRemaining(timeRemaining)}
                    </span>
                  </div>
                )}
              </div>
              {tokenInfo.error && (
                <p className="mt-2 text-sm text-red-700">{tokenInfo.error}</p>
              )}
              {tokenInfo.access_token && (
                <p className="mt-2 text-xs text-green-700 font-mono break-all">
                  Token: {tokenInfo.access_token.substring(0, 50)}...
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="flex space-x-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setResults(null);
                }}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon size={18} />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Forms */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Form Panel */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">
            {tabs.find((t) => t.id === activeTab)?.name}
          </h2>

          {/* Address List Form */}
          {activeTab === 'address' && (
            <form onSubmit={(e) => handleSubmit(e, 'address')} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Postal Code *
                </label>
                <input
                  type="text"
                  value={addressForm.PostalCode}
                  onChange={(e) => setAddressForm({ PostalCode: e.target.value })}
                  placeholder="e.g., 8041"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={addressMutation.isLoading}
                className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {addressMutation.isLoading ? 'Searching...' : 'Get Address List'}
              </button>
            </form>
          )}

          {/* Fiber Search Form */}
          {activeTab === 'fiber' && (
            <form onSubmit={(e) => handleSubmit(e, 'fiber')} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street ID *
                </label>
                <input
                  type="text"
                  value={fiberForm.StreetID}
                  onChange={(e) => setFiberForm({ ...fiberForm, StreetID: e.target.value })}
                  placeholder="Street ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Number *
                </label>
                <input
                  type="text"
                  value={fiberForm.StreetNumber}
                  onChange={(e) => setFiberForm({ ...fiberForm, StreetNumber: e.target.value })}
                  placeholder="Street Number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Flat Number (Optional)
                </label>
                <input
                  type="text"
                  value={fiberForm.greek_flat_number}
                  onChange={(e) => setFiberForm({ ...fiberForm, greek_flat_number: e.target.value })}
                  placeholder="Flat Number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                disabled={fiberMutation.isLoading}
                className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {fiberMutation.isLoading ? 'Searching...' : 'Search Fiber Availability'}
              </button>
            </form>
          )}

          {/* ONT Physical Status Form */}
          {activeTab === 'ontPhysical' && (
            <form onSubmit={(e) => handleSubmit(e, 'ontPhysical')} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Circuit Number *
                </label>
                <input
                  type="text"
                  value={ontPhysicalForm.CircuitNumber}
                  onChange={(e) => setOntPhysicalForm({ CircuitNumber: e.target.value })}
                  placeholder="e.g., 30282721"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={ontPhysicalMutation.isLoading}
                className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {ontPhysicalMutation.isLoading ? 'Checking...' : 'Get ONT Physical Status'}
              </button>
            </form>
          )}

          {/* ONT Ethernet Status Form */}
          {activeTab === 'ontEth' && (
            <form onSubmit={(e) => handleSubmit(e, 'ontEth')} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Circuit Number *
                </label>
                <input
                  type="text"
                  value={ontEthForm.CircuitNumber}
                  onChange={(e) => setOntEthForm({ CircuitNumber: e.target.value })}
                  placeholder="e.g., 30282721"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={ontEthMutation.isLoading}
                className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {ontEthMutation.isLoading ? 'Checking...' : 'Get ONT Ethernet Status'}
              </button>
            </form>
          )}

          {/* Connection Details Form */}
          {activeTab === 'connection' && (
            <form onSubmit={(e) => handleSubmit(e, 'connection')} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Circuit Number
                </label>
                <input
                  type="text"
                  value={connectionForm.CircuitNo}
                  onChange={(e) => setConnectionForm({ ...connectionForm, CircuitNo: e.target.value })}
                  placeholder="Circuit Number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer Serial Number
                </label>
                <input
                  type="text"
                  value={connectionForm.CustSerialNumber}
                  onChange={(e) => setConnectionForm({ ...connectionForm, CustSerialNumber: e.target.value })}
                  placeholder="Customer Serial Number"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <p className="text-sm text-gray-500">At least one field is required</p>
              <button
                type="submit"
                disabled={connectionMutation.isLoading}
                className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {connectionMutation.isLoading ? 'Fetching...' : 'Get Connection Details'}
              </button>
            </form>
          )}

          {/* Planned Date Form */}
          {activeTab === 'plannedDate' && (
            <form onSubmit={(e) => handleSubmit(e, 'plannedDate')} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Latitude *
                </label>
                <input
                  type="text"
                  value={plannedDateForm.latitude}
                  onChange={(e) => setPlannedDateForm({ ...plannedDateForm, latitude: e.target.value })}
                  placeholder="e.g., 34.75043"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Longitude *
                </label>
                <input
                  type="text"
                  value={plannedDateForm.longitude}
                  onChange={(e) => setPlannedDateForm({ ...plannedDateForm, longitude: e.target.value })}
                  placeholder="e.g., 32.47151"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>
              <button
                type="submit"
                disabled={plannedDateMutation.isLoading}
                className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {plannedDateMutation.isLoading ? 'Fetching...' : 'Get Planned Date'}
              </button>
            </form>
          )}
        </div>

        {/* Results Panel */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Results</h2>
            {results && results.response_xml && !results.error && (
              <div className="flex gap-2">
                <button
                  onClick={() => setViewMode('readable')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                    viewMode === 'readable'
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <FileText size={16} />
                  Readable
                </button>
                <button
                  onClick={() => setViewMode('xml')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${
                    viewMode === 'xml'
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Code size={16} />
                  XML
                </button>
              </div>
            )}
          </div>
          {results ? (
            <div className="space-y-4">
              {results.error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                  <p className="font-semibold">Error:</p>
                  <p>{results.error}</p>
                </div>
              ) : (
                <>
                  {results.response_xml && (
                    <div>
                      {viewMode === 'readable' ? (
                        <>
                          <h3 className="font-semibold mb-3 text-lg">Response Data:</h3>
                          <div className="bg-white rounded-lg p-4 border-2 border-gray-300 shadow-sm overflow-y-auto overflow-x-auto max-h-[600px]" style={{ maxHeight: '600px', overflowY: 'scroll' }}>
                            <pre className="text-sm text-gray-800 whitespace-pre font-mono" style={{ margin: 0 }}>
                              {jsonToText(parseXmlToJson(results.response_xml))}
                            </pre>
                          </div>
                        </>
                      ) : (
                        <>
                          <h3 className="font-semibold mb-2">Response XML:</h3>
                          <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-300 shadow-sm">
                            <pre className="text-xs overflow-auto max-h-96 whitespace-pre-wrap break-words font-mono">
                              {results.response_xml}
                            </pre>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {results.request_xml && viewMode === 'xml' && (
                    <div>
                      <h3 className="font-semibold mb-2">Request XML:</h3>
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <pre className="text-xs overflow-auto max-h-48 whitespace-pre-wrap break-words font-mono">
                          {results.request_xml}
                        </pre>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <p>No results yet. Submit a query to see results here.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CytaApi;
